import React, { useEffect, useState } from 'react';
import { ScanBarcode, MessageSquare, Lock, Heart } from 'lucide-react';
import { useLang } from '../i18n';
import { useConsent } from '../consent';
import { useReviews } from '../reviews';
import { StarRating } from './StarRating';
import api from '../api';

export const Footer = ({ onOpenLegal, onOpenContact }) => {
  const { t, lang } = useLang();
  const { openSettings } = useConsent();
  const { summary } = useReviews();
  const year = new Date().getFullYear();
  const base = lang === 'en' ? '/en' : '';

  // Admin-only unread messages counter: only visible if an admin session exists
  // in this browser (password stored after logging in at /admin).
  const [unread, setUnread] = useState(null);
  useEffect(() => {
    let mounted = true;
    let pw = null;
    try { pw = sessionStorage.getItem('bc_admin_pw'); } catch (e) { /* noop */ }
    if (!pw) return undefined;
    api.adminListContact(pw, 1)
      .then((res) => { if (mounted) setUnread(res.data.unread ?? 0); })
      .catch(() => { /* silent: not logged in / invalid */ });
    return () => { mounted = false; };
  }, []);

  const scrollToReviews = () => {
    const el = document.getElementById('reviews');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const countWord = summary.count === 1 ? t('reviews_one') : t('reviews_many');
  const seoLinks = [
    ['qr-code-generator', lang === 'en' ? 'QR Code Generator' : 'QR-Code Generator'],
    ['barcode-generator', lang === 'en' ? 'Barcode Generator' : 'Barcode Generator'],
    ['ean-13-generator', 'EAN-13'],
    ['ean-8-generator', 'EAN-8'],
    ['upc-generator', 'UPC'],
    ['code-128-generator', 'Code 128'],
    ['code-39-generator', 'Code 39'],
    ['isbn-barcode-generator', 'ISBN'],
    ['gs1-barcode-generator', 'GS1-128'],
    ['barcode-labels', lang === 'en' ? 'Barcode labels' : 'Barcode-Etiketten'],
  ];
  const guideLinks = [
    ['how-to-create-a-barcode', lang === 'en' ? 'Create a barcode' : 'Barcode erstellen'],
    ['ean-vs-upc', lang === 'en' ? 'EAN vs. UPC' : 'EAN vs. UPC'],
    ['what-is-a-gtin', lang === 'en' ? 'What is a GTIN?' : 'Was ist eine GTIN?'],
    ['which-barcode-for-what-purpose', lang === 'en' ? 'Which barcode?' : 'Welcher Barcode?'],
    ['how-to-print-barcodes', lang === 'en' ? 'Print barcodes' : 'Barcodes drucken'],
    ['data-matrix-vs-qr-code', lang === 'en' ? 'Data Matrix vs. QR' : 'Data Matrix vs. QR'],
    ['barcode-size-and-quiet-zone', lang === 'en' ? 'Barcode size' : 'Barcode-Größe'],
    ['barcodes-for-amazon-ebay-google', lang === 'en' ? 'Sell on marketplaces' : 'Auf Marktplätzen verkaufen'],
  ];
  const legalSlug = { imprint: 'impressum', privacy: 'datenschutz', cookie_policy: 'cookie-richtlinie', terms: 'agb' };
  const links = [
    ['imprint', t('imprint')],
    ['privacy', t('privacy')],
    ['cookie_policy', t('cookie_policy')],
    ['terms', t('terms')],
  ];
  return (
    <footer className="mt-10 border-t bg-card/60" data-testid="app-footer">
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-6 sm:px-6 lg:px-8">
        {/* Compact ratings widget */}
        <button
          type="button"
          onClick={scrollToReviews}
          data-testid="footer-rating-widget"
          className="flex items-center gap-2 self-start rounded-lg border bg-secondary/40 px-3 py-1.5 transition-colors hover:bg-secondary"
          aria-label={t('reviews_title')}
        >
          <StarRating value={summary.count ? summary.average : 0} size={16} />
          <span className="text-xs text-muted-foreground">
            {summary.count > 0
              ? `${Number(summary.average).toFixed(1)} · ${summary.count} ${countWord}`
              : t('rate_button')}
          </span>
        </button>
        <nav className="flex flex-wrap gap-x-4 gap-y-1.5" aria-label={lang === 'en' ? 'Generators' : 'Generatoren'}>
          {seoLinks.map(([slug, label]) => (
            <a
              key={slug}
              href={`${base}/${slug}/`}
              data-testid={`footer-seo-${slug}`}
              className="text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
            >
              {label}
            </a>
          ))}
        </nav>
        <nav className="flex flex-wrap gap-x-4 gap-y-1.5" aria-label={lang === 'en' ? 'Guides' : 'Ratgeber'}>
          <a
            href={lang === 'en' ? '/en/guides/' : '/ratgeber/'}
            data-testid="footer-guides-index"
            className="text-xs font-semibold text-foreground underline-offset-2 hover:underline"
          >
            {lang === 'en' ? 'Guides:' : 'Ratgeber:'}
          </a>
          {guideLinks.map(([slug, label]) => (
            <a
              key={slug}
              href={`${base}/${slug}/`}
              data-testid={`footer-guide-${slug}`}
              className="text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
            >
              {label}
            </a>
          ))}
          <a
            href={`${base}/about/`}
            data-testid="footer-about"
            className="text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
          >
            {lang === 'en' ? 'About us' : 'Über uns'}
          </a>
        </nav>
        <div className="flex flex-col items-start justify-between gap-3 border-t pt-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ScanBarcode className="h-4 w-4 text-primary" />
            <span className="font-heading font-semibold text-foreground">Barcode Studio</span>
            <span>&copy; {year}</span>
            <a
              href="/?admin=1"
              data-testid="footer-admin-link"
              rel="noopener"
              aria-label={t('admin_area')}
              title={t('admin_area')}
              className="ml-1 inline-flex items-center rounded p-1 text-muted-foreground/50 transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Lock className="h-3.5 w-3.5" />
            </a>
          </div>
          <nav className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {links.map(([key, label]) => (
              <a
                key={key}
                href={`${base}/${legalSlug[key]}/`}
                onClick={(e) => {
                  // Normal click opens the in-app modal; modified/middle clicks
                  // follow the crawlable static page (new tab etc.).
                  if (!(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1)) {
                    e.preventDefault();
                    onOpenLegal(key);
                  }
                }}
                data-testid={`footer-${key}`}
                className="text-sm text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
              >
                {label}
              </a>
            ))}
            <button
              onClick={openSettings}
              data-testid="footer-cookie-settings"
              className="text-sm text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
            >
              {t('cookie_settings')}
            </button>
            <button
              onClick={onOpenContact}
              data-testid="footer-contact"
              className="text-sm text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
            >
              {t('contact')}
            </button>
            <a
              href="/supporter"
              data-testid="footer-supporter"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-2 transition-colors hover:underline"
            >
              <Heart className="h-3.5 w-3.5" />
              {lang === 'en' ? 'Support us' : 'Unterstützen'}
            </a>
            {unread !== null && (
              <a
                href="/?admin=1"
                data-testid="footer-admin-messages"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                {t('admin_messages_link')}
                {unread > 0 && (
                  <span
                    className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground"
                    data-testid="footer-unread-badge"
                  >
                    {unread} {t('admin_new_suffix')}
                  </span>
                )}
              </a>
            )}
          </nav>
        </div>
        <p className="text-xs text-muted-foreground">{t('footer_rights')}</p>
      </div>
    </footer>
  );
};
